/**
 * WHETHER THIS PAGE IS STILL BEING TOLD ANYTHING — #308.
 *
 * WHAT WENT WRONG. On live, 30 August 2026, Vinnie had the board open and two
 * cards raised elsewhere never arrived. He searched for them and got nothing,
 * which is worse than a blank screen: **an empty search result is an assertion,
 * and that one was false.** The cards existed, the board was right, and the page
 * was old. The only way back was a reload, and nothing told him to.
 *
 * THE MECHANISM. A listener reads once when it attaches and re-reads whenever
 * `postgres_changes` announces something. There is nothing in between. If the
 * channel goes away — a laptop sleeping, a blip, a socket timing out — Realtime
 * rejoins it, and every change made while it was gone was announced to nobody.
 * Nothing triggers a fresh read, so the page keeps showing what it had at the
 * moment it stopped listening, for as long as it stays open.
 *
 * ── TWO ANSWERS, AND THE FIRST ONE IS THE FIX ────────────────────────────────
 *
 * BACKFILL, in src/backend/supabase.ts: a channel that comes back re-reads,
 * because everything it missed is still sitting in the database waiting to be
 * asked for. That closes the case this card was raised about, and it closes it
 * without anybody noticing anything happened, which is the right outcome.
 *
 * SAY SO, here: a channel that does not come back cannot be backfilled by
 * anything, and a board that is quietly out of date is worse than one that is
 * visibly broken. So the page stops pretending.
 *
 * WHY THIS IS A MODULE AND NOT A FIELD ON THE STORE. The thing that knows is
 * the backend seam, and the seam does not import the store — that is the whole
 * point of it being a seam. This is the smallest thing that lets one tell the
 * other: two functions and a boolean, no framework.
 *
 * ⛔ AND IT IS NOT A `useSyncExternalStore` SELECTOR. Read the note on NOBODY in
 * src/signatories.ts. A subscription that hands back a fresh value every time it
 * is read is how the Releases screen hung on live for a fortnight; this hands
 * back a boolean, which is the same boolean until it genuinely changes.
 */

/**
 * Channels that are currently attached, by name.
 *
 * COUNTED RATHER THAN A FLAG, because a board has several listeners at once and
 * they do not fail together. One dropping is enough to make the page wrong, and
 * one recovering is not enough to make it right again.
 */
const away = new Set<string>();

let told: ((listening: boolean) => void)[] = [];

/** True when every listener that has ever attached is currently attached. */
export function listening(): boolean {
  return away.size === 0;
}

/**
 * Said by the seam, once per channel, whenever its state genuinely changes.
 *
 * Idempotent on purpose: Realtime reports `SUBSCRIBED` on every rejoin and
 * `CLOSED` on every teardown, including the ordinary ones that happen when
 * somebody switches board. Only a change in the ANSWER is passed on.
 */
export function nowListening(channel: string, ok: boolean): void {
  const was = listening();
  if (ok) away.delete(channel);
  else away.add(channel);
  const now = listening();
  if (was === now) return;
  for (const fn of told) {
    try {
      fn(now);
    } catch {
      /* A watcher that throws must not become the outage — the same rule as
         the fault watchers in src/trail.ts, and for the same reason. */
    }
  }
}

/** A listener being let go on purpose is not an outage. */
export function stopWatching(channel: string): void {
  nowListening(channel, true);
}

export function whenLiveChanges(fn: (listening: boolean) => void): () => void {
  told.push(fn);
  return () => {
    told = told.filter((f) => f !== fn);
  };
}

/** For the suite, which has to start each case from the same place. */
export function forgetLiveState(): void {
  away.clear();
  told = [];
}
